/**
 * 产品详情内容增强：为目录中各产品补充更丰富的介绍、应用场景与交付说明。
 * 在 buildProducts / buildProductDetailMeta 中自动合并。
 */

const DEFAULT_DELIVERY = [
  { title: '需求调研', desc: '梳理业务场景、功能边界与接口清单，输出需求说明与实施建议。' },
  { title: '方案设计', desc: '提供架构设计、设备选型或系统模块划分，明确里程碑与验收标准。' },
  { title: '开发集成', desc: '按约定周期完成开发、联调与现场安装调试，支持阶段性演示。' },
  { title: '培训交付', desc: '提供操作培训、维护说明与文档资料，确保用户可独立使用。' },
  { title: '售后支持', desc: '质保期内故障响应与远程协助，可按需签订运维与升级服务。' },
]

const EDU_DELIVERY = [
  { title: '场地勘察', desc: '结合实训室面积、电源条件与安全规范，给出布局与点位建议。' },
  { title: '设备供货', desc: '按标书或清单配置供货，含备品备件、工具与随机资料。' },
  { title: '安装调试', desc: '现场安装、通电调试与功能验收，确保装置符合技术指标。' },
  { title: '师资培训', desc: '面向教师开展设备操作、课程对接与竞赛/考核要点培训。' },
  { title: '课程资源', desc: '可提供实验指导书、工单任务书等配套资源（按项目约定）。' },
]

function mergeArrays(...lists) {
  for (const list of lists) {
    if (Array.isArray(list) && list.length) return list
  }
  return []
}

function expandDetailFromBullets(bullets, extra = []) {
  const base = [...(bullets || []), ...extra]
  return base.filter(Boolean).slice(0, 8)
}

/** 按分类的默认增强内容 */
const categoryEnrichment = {
  software: {
    applications: [
      { title: '离散制造', desc: '工单下发、工序报工、质量追溯与设备数据采集一体化。' },
      { title: '装配产线', desc: '产线看板、Andon、物料拉动与 WMS/AGV 协同。' },
      { title: '院校示范', desc: '数字工厂、MES/ERP 教学演示与竞赛支撑环境。' },
    ],
    deliveryItems: DEFAULT_DELIVERY,
  },
  'energy-system': {
    applications: [
      { title: '新能源实训', desc: '太阳能、风能原理验证与双轴跟踪控制实验。' },
      { title: '能源监控', desc: '发电数据采集、能效看板与远程监测。' },
      { title: '光伏运维', desc: '多电站集中管理、逆变器监测与报警闭环。' },
    ],
    deliveryItems: [...EDU_DELIVERY.slice(0, 3), ...DEFAULT_DELIVERY.slice(3)],
  },
  iiot: {
    applications: [
      { title: '工业互联网实训', desc: '传感器、网关、云平台联调与数据上云实验。' },
      { title: '产线数字化', desc: '设备联网、环境能源监控与 APP 看板展示。' },
    ],
    deliveryItems: DEFAULT_DELIVERY,
  },
  humanoid: {
    applications: [
      { title: '展厅导览', desc: '语音交互、路径引导与品牌展示。' },
      { title: '科研实验', desc: '运动控制、视觉导航与二次开发验证。' },
      { title: '教学实训', desc: '机器人结构认知、编程与系统集成课程。' },
    ],
    deliveryItems: DEFAULT_DELIVERY,
  },
  quadruped: {
    applications: [
      { title: '电力巡检', desc: '变电站、配电室可见光/红外巡检与数据回传。' },
      { title: '石化园区', desc: '复杂地形巡逻、气体检测与远程操控。' },
      { title: '科研教学', desc: '步态控制、SLAM 与载荷扩展实验。' },
    ],
    deliveryItems: DEFAULT_DELIVERY,
  },
  'ai-custom': {
    applications: [
      { title: '机器视觉', desc: '缺陷检测、定位抓取与深度学习模型部署。' },
      { title: '嵌入式 AI', desc: '边缘推理、控制器联调与算法验证。' },
      { title: '教学竞赛', desc: '便携式/桌面式平台支撑课程与技能训练。' },
    ],
    deliveryItems: DEFAULT_DELIVERY,
  },
  drone: {
    applications: [
      { title: '电力巡检', desc: '线路、杆塔、光伏组件 aerial 巡检与图像分析。' },
      { title: '园区安防', desc: '定时巡航、异常识别与视频回传。' },
      { title: '物流运输', desc: '短途重载物资转运与应急投送。' },
    ],
    deliveryItems: DEFAULT_DELIVERY,
  },
  agv: {
    applications: [
      { title: '仓储物流', desc: '入库、出库、拣选与线边配送自动化。' },
      { title: '柔性制造', desc: '工位间物料转运与 MES/WMS 任务联动。' },
      { title: '实训基地', desc: 'SLAM、调度算法与多机协同教学。' },
    ],
    deliveryItems: DEFAULT_DELIVERY,
  },
  'mobile-platform': {
    applications: [
      { title: '二次开发', desc: '开放底盘 SDK，快速搭建上层应用。' },
      { title: '复合机器人', desc: '移动底盘 + 机械臂/视觉/语音一体方案。' },
      { title: '室内物流', desc: '全向/差速底盘适配狭窄通道与灵活路径。' },
    ],
    deliveryItems: DEFAULT_DELIVERY,
  },
  'automation-line': {
    applications: [
      { title: '装配产线', desc: '自动上料、压装、检测与下料整线集成。' },
      { title: '包装检测', desc: '视觉检测、贴标、装箱与码垛联动。' },
      { title: '技改升级', desc: '在现有产线基础上增加机器人或视觉单元。' },
    ],
    deliveryItems: DEFAULT_DELIVERY,
  },
  'smart-line': { deliveryItems: EDU_DELIVERY },
  'estun-trainer': { deliveryItems: EDU_DELIVERY },
  'auto-line-trainer': { deliveryItems: EDU_DELIVERY },
  'robot-ops': { deliveryItems: EDU_DELIVERY },
  'sensor-box': { deliveryItems: EDU_DELIVERY },
  'sensor-lab': { deliveryItems: EDU_DELIVERY },
  electrician: { deliveryItems: EDU_DELIVERY },
  'electric-electronic': { deliveryItems: EDU_DELIVERY },
  'network-platform': { deliveryItems: EDU_DELIVERY },
  'plc-trainer': { deliveryItems: EDU_DELIVERY },
  'mechanical-assembly': { deliveryItems: EDU_DELIVERY },
  'welding-station': { deliveryItems: EDU_DELIVERY },
  'digital-resource': { deliveryItems: DEFAULT_DELIVERY },
  'digital-textbook': { deliveryItems: DEFAULT_DELIVERY },
  'teaching-library': { deliveryItems: DEFAULT_DELIVERY },
  'competition-support': { deliveryItems: DEFAULT_DELIVERY },
  'research-cooperation': { deliveryItems: DEFAULT_DELIVERY },
  'original-products': {
    applications: [
      { title: '产线集成', desc: '作为自动化产线中的功能模块或独立工位交付。' },
      { title: '教学示范', desc: '院校智能制造、机器人等专业实训与展示。' },
      { title: '定制研发', desc: '基于项目积累的原型机与专项设备开发。' },
    ],
    deliveryItems: DEFAULT_DELIVERY,
  },
  'case-showcase': {
    applications: [
      { title: '实训室规划', desc: '空间布局、动线设计与设备点位预排。' },
      { title: '方案投标', desc: '效果图配合清单用于项目申报与招投标。' },
      { title: '施工落地', desc: '效果图深化后配套成套设备供货与安装。' },
    ],
    deliveryItems: [...DEFAULT_DELIVERY.slice(0, 2), ...EDU_DELIVERY.slice(2)],
  },
  'service-clients': {
    applications: [
      { title: '企业客户', desc: '科教设备、制造企业与科技服务机构。' },
      { title: '院校客户', desc: '高职、本科及技师学院实训建设。' },
      { title: '公共服务', desc: '科协、社区与科技馆科普与培训活动。' },
    ],
    deliveryItems: DEFAULT_DELIVERY,
  },
}

/** 自研云平台整包说明（系统模块共用） */
const systemPackEnrichment = {
  'smart-power-cloud': {
    platformName: '智慧用电云平台',
    intro:
      '天津高仁智慧用电云平台面向工厂、园区、配电室及院校实训场景，提供电力监测、运行报表、用电分析、四象限电能、分时用电与自定义报表等能力，支持私有化本地部署。',
    introPoints: [
      '按组织机构与回路树管理配电数据，支持五分钟粒度曲线',
      '运行报表、同比环比、四象限电能等多维度分析',
      '自定义报表与饼图，支持查询、导出与权限分级',
    ],
    applications: [
      { title: '工厂配电', desc: '主进线柜、动力站回路实时监测与历史分析。' },
      { title: '园区能源', desc: '多站点、多回路集中管理与能效看板。' },
      { title: '院校实训', desc: '智慧用电、能源管理课程演示与实验。' },
    ],
  },
  'smart-pv-cloud': {
    platformName: '智慧光伏发电云平台',
    intro:
      '天津高仁智慧光伏发电云平台支持多电站地图集中管理，覆盖逆变器监测、报警闭环、网关通讯与服务运维监控，适用于分布式光伏运维与新能源实训教学。',
    introPoints: [
      '全国地图标注电站，日/月/年发电量与收益统计',
      '逆变器效率色块表、报警分级与一键确认',
      '网关在线率、服务器 CPU/内存/JVM 运行监控',
    ],
    applications: [
      { title: '光伏运维', desc: '多站集中监控、逆变器状态与报警处理。' },
      { title: '收益分析', desc: '发电量、收益与社会效益综合统计。' },
      { title: '新能源教学', desc: '光伏电站运行数据与运维流程实训。' },
    ],
  },
}

/** 按产品名称的专属增强（优先于分类默认） */
const productEnrichmentByName = {
  'MES 生产制造执行系统': {
    tagline: '打通计划、执行、质量与设备数据的制造执行中枢。',
    summary:
      '面向离散制造与装配产线，覆盖工单管理、工艺执行、质量追溯、现场数据采集与看板展示，可与 ERP、WMS、PLC 及条码系统对接。',
    intro:
      'MES 生产制造执行系统是连接企业计划层与车间执行层的关键平台。天津高仁科技基于多个制造与实训项目经验，提供可模块化部署的 MES 定制开发服务，帮助企业实现生产过程透明化、质量可追溯与数据实时联动。',
    introPoints: [
      '支持工单下发、工序报工、在制品跟踪与异常 Andon',
      '质量数据与批次/序列号绑定，支持正向与反向追溯',
      '设备、条码、AGV、ERP、WMS 等多系统接口可扩展',
    ],
    detailPoints: [
      '工单与工艺：支持多工序路线、工艺参数下发与变更记录',
      '质量管理：检验项配置、不良品处理、SPC 与报表导出',
      '数据采集：PLC、机器人、传感器、人工报工多源采集',
      '看板展示：产线 OEE、产量、良率、设备状态实时可视化',
      '部署灵活：支持本地私有化、混合云及教学示范环境',
    ],
    highlightParams: [
      { value: '模块化', label: '部署方式' },
      { value: '多系统', label: '接口对接' },
      { value: '可追溯', label: '质量管控' },
      { value: '可定制', label: '交付模式' },
    ],
  },
  'ERP 企业经营管理系统': {
    tagline: '采购、库存、订单与财务协同的一体化经营中台。',
    summary:
      '围绕制造企业经营管理流程，构建采购、库存、销售订单、生产协同与财务数据联动的 ERP 系统，支持本地或混合部署。',
    intro:
      'ERP 系统帮助企业规范经营流程、降低库存冗余并提升订单交付效率。高仁科技可根据企业规模与行业特点，定制开发符合实际管理颗粒度的 ERP 模块，并与 MES、WMS 等系统形成数据闭环。',
    introPoints: [
      '采购管理：供应商档案、采购申请与到货检验流程',
      '库存协同：多仓库、批次管理与安全库存预警',
      '订单财务：销售订单、生产计划与应收应付联动',
    ],
    detailPoints: [
      '采购管理：供应商、采购申请、到货检验与对账流程',
      '库存管理：多仓库、批次、安全库存与盘点业务',
      '订单协同：销售订单、生产计划与发货状态联动',
      '财务协同：应收应付、成本归集与经营报表',
      '权限审批：可配置审批流与角色权限体系',
    ],
  },
  'WMS 仓储物流管理系统': {
    tagline: '承接入库、出库、库位与 AGV 任务协同的仓储大脑。',
    summary:
      '面向仓内物流与柔性制造搬运场景，提供入库、出库、库位管理、任务调度及与 AGV/AMR、上位系统的对接能力。',
    intro:
      'WMS 仓储物流管理系统适用于工厂线边仓、中央仓及院校物流实训基地。系统可定义库位策略、拣选规则与任务优先级，并与 AGV 调度系统联动，实现「系统下发任务—设备执行—状态回传」的闭环。',
    detailPoints: [
      '入库管理：收货、质检、上架与库存初始化',
      '出库管理：波次拣选、复核、打包与发货确认',
      '库位管理：库区/库位/容器层级与可视化库存',
      '任务调度：与 AGV、输送线、堆垛机等设备任务对接',
      '报表分析：周转率、呆滞料、作业效率统计',
    ],
  },
  'OA 协同办公系统': {
    tagline: '审批、文档、协同与移动办公的一体化数字平台。',
    summary:
      '搭建企业级协同办公平台，覆盖流程审批、公文流转、日程协作、知识文档与移动端办公，支持私有化部署。',
    intro:
      'OA 协同办公系统适用于制造企业与项目型组织的内部协同。高仁科技可基于企业现有制度与流程，定制表单、审批链与权限模型，并与 ERP、HR 等系统做单点登录或数据同步。',
    detailPoints: [
      '流程引擎：可视化流程设计、会签、加签与退回',
      '文档管理：版本控制、全文检索与权限分级',
      '移动办公：待办推送、移动审批与消息通知',
      '组织权限：部门、岗位、角色与数据范围控制',
    ],
  },
  '数字孪生与可视化平台': {
    tagline: '产线与设备的可视化映射、监控与仿真演示平台。',
    summary:
      '将物理产线、设备运行状态映射为三维或二维数字模型，支持实时监控、历史回放、告警看板与教学演示，可与 PLC、MES、传感器数据实时对接。',
    intro:
      '数字孪生与可视化平台通过数据驱动的方式还原产线布局与设备行为，可用于生产监控、故障定位、方案演示及院校智能制造教学。高仁科技支持按产线规模定制建模范围、刷新频率与渲染终端，并与工业互联网实训平台形成虚实联动。',
    introPoints: [
      '三维/二维可视化：产线布局、设备动作与工艺节拍建模',
      '数据联动：PLC、机器人、传感器实时状态驱动',
      '告警看板：阈值规则、异常高亮与事件记录',
    ],
    detailPoints: [
      '模型构建：产线布局、设备动作与工艺节拍建模',
      '数据映射：实时采集点位绑定与状态驱动动画',
      '监控告警：阈值规则、异常高亮与事件记录',
      '教学应用：虚实联动、故障注入与方案对比演示',
      '部署方式：本地私有化、大屏展示与多端访问',
    ],
  },
  '预见性故障监测与点检系统': {
    tagline: '设备状态监测、趋势分析与故障预警的运维平台。',
    summary:
      '面向设备运维与产线巡检，提供点检任务、振动/温度/电流等数据采集、趋势分析与预警提醒，适合工厂运维与教学示范。',
    intro:
      '预见性故障监测与点检系统帮助用户从「事后维修」转向「状态维护」。系统可配置点检路线、采集项与告警规则，并与振动、温度、电流、电压等传感器或手持终端联动，形成「监测—预警—工单—处理」闭环。',
    introPoints: [
      '点检任务：计划生成、移动端执行与漏检提醒',
      '状态监测：多参数曲线、极值标注与同比对比',
      '预警规则：阈值、趋势与组合条件告警',
    ],
    detailPoints: [
      '点检任务：计划生成、移动端执行与漏检提醒',
      '状态监测：振动、温度、电流、电压等多参数曲线',
      '预警规则：阈值、趋势与组合条件告警',
      '运维闭环：工单派发、处理记录与知识库积累',
      '历史分析：趋势对比、报表导出与决策支持',
    ],
  },
  '节能管理系统': {
    tagline: '能耗采集、分项统计与能效分析的能源管理平台。',
    summary:
      '围绕电、水、气等能源介质，提供采集、分项计量、趋势分析、报表与节能改造建议，适用于工厂、园区与实训基地。',
    intro:
      '节能管理系统与自研智慧用电云平台可形成互补，支持多回路、多站点能源数据汇聚，帮助管理者识别高耗能环节并评估节能措施效果。',
    detailPoints: [
      '能耗采集：表计、网关、485/Modbus 等多协议接入',
      '分项统计：按区域、产线、设备维度拆分能耗',
      '分析报表：日/月/年报表、同比环比与自定义报表',
      '节能评估：基准线对比与改造前后效果跟踪',
    ],
  },
  '移动人型机器人（PL-UNIVERSE）': {
    tagline: '双臂移动人型机器人，适用于导览、交互与科研开发。',
    summary:
      '集成移动底盘、双臂操作、视觉与语音交互能力，可用于展厅服务、实验室科研及机器人系统集成教学。',
    intro:
      '移动人型机器人方案强调「移动 + 操作 + 交互」一体化。高仁科技提供整机定制、二次开发接口与场景化应用集成，支持根据客户需求配置传感器、末端工具与上层软件。',
    detailPoints: [
      '移动导航：SLAM/视觉导航，适应室内复杂环境',
      '双臂协作：抓取、递送、简单装配与示教编程',
      '人机交互：语音唤醒、对话与触摸屏信息展示',
      '开放接口：ROS/SDK 支持算法与上层系统联调',
    ],
  },
  '双足人型机器人（UNITREE）': {
    tagline: '双足人型机器人整机方案，面向展示与运动控制研究。',
    summary:
      '基于成熟双足平台，提供运动控制、平衡算法验证及视觉/语音扩展集成服务。',
    intro:
      '双足人型机器人适用于科技展示、运动控制科研与高级机器人课程。高仁科技可协助客户完成场景部署、安全围栏设计、交互软件定制与维护培训。',
    detailPoints: [
      '运动能力：行走、转向、简单动作编排',
      '扩展载荷：相机、激光雷达、麦克风阵列等',
      '开发支持：运动学/动力学实验与仿真对接',
      '场景定制：展厅动线、讲解词与互动流程',
    ],
  },
  '四足巡检机器狗（浅灰款）': {
    tagline: '工业级四足巡检机器人，适应楼梯与复杂地形。',
    summary:
      '面向电力、石化、园区等巡检场景，支持可见光、红外及多种传感器载荷，远程操控与任务规划。',
    intro:
      '四足巡检机器狗可在传统轮式设备难以到达的区域执行巡检任务。高仁科技提供任务路线规划、数据采集回传、报警规则与后台管理平台的一体化交付。',
    detailPoints: [
      '地形适应：楼梯、坡道、碎石与室内外混合环境',
      '巡检载荷：可见光、红外热像、气体检测等可选',
      '远程操控：实时图传、任务录制与自主巡检',
      '数据管理：巡检报告、缺陷标注与历史对比',
    ],
  },
  '智能叉车 AGV': {
    tagline: '自动叉取、堆垛与搬运的工业级 AGV 方案。',
    summary:
      '适用于托盘物料的自动叉取、堆垛与线边配送，支持 SLAM 导航与 WMS/MES 任务对接。',
    intro:
      '智能叉车 AGV 解决传统人工叉车效率低、安全风险高的问题。高仁科技从地图构建、调度算法到上位系统接口提供完整定制开发，并可与现有仓储流程深度融合。',
    detailPoints: [
      '导航方式：激光 SLAM / 反光板 / 混合导航可选',
      '作业能力：自动叉取、堆垛、放货与充电管理',
      '调度系统：多车避让、任务优先级与交通管制',
      '系统对接：WMS、MES、ERP 与 AGV 任务接口',
    ],
  },
  '潜伏顶升 AGV 底盘': {
    tagline: '低轮廓潜伏顶升底盘，适合货架底部搬运。',
    summary:
      '潜入式顶升 AGV 适用于电商、制造线边仓等场景，负载可定制，支持多机调度。',
    intro:
      '潜伏顶升 AGV 通过潜入货架底部并顶升搬运，最大化利用仓储空间。高仁科技提供底盘选型、顶升机构设计、调度软件与现场调试全流程服务。',
    detailPoints: [
      '低轮廓设计：适应标准货架底部空间',
      '顶升搬运：额定负载按项目定制',
      '精确定位：二维码/SLAM 组合定位可选',
      '充电策略：自动充电与电量管理',
    ],
  },
  '智能智造产线应用综合实训平台': {
    tagline: '集成机器人、PLC、视觉、RFID 与数字孪生的智能制造实训产线。',
    summary:
      '六轴工业机器人核心，融合 PLC 控制、机器视觉、RFID、组态与数字孪生等技术，满足教学、竞赛与示范工厂建设需求。',
    intro:
      '本平台对标智能制造领域核心技能，采用真实工业组件构建缩微产线，学生可完成从编程、调试到系统集成与故障排查的全流程实训，院校可用于专业建设、1+X 证书与技能大赛训练。',
    introPoints: [
      '交流电源：单相三线～220V±10% 50Hz，装置容量≤2.2kVA',
      '设备尺寸：3300mm×1000mm×1700mm（L×W×H）',
      '安全保护：急停、漏电保护、过流保护齐全',
    ],
    detailPoints: [
      '工业机器人应用技术：搬运、码垛、装配等典型工艺',
      'PLC 与组态：逻辑控制、HMI 画面与数据采集',
      '机器视觉：定位、检测、读码与质量判别实验',
      'RFID 与物流：物料识别、追溯与仓储联动',
      '数字孪生：产线虚拟映射与监控演示',
    ],
    applications: [
      { title: '专业实训', desc: '智能制造、机电一体化、工业机器人等专业核心课程。' },
      { title: '技能大赛', desc: '国赛、世赛及行业赛项训练与考核。' },
      { title: '示范工厂', desc: '院校数字工厂、产教融合示范基地展示。' },
    ],
  },
  '工业机器人技能实训装置（埃斯顿）': {
    tagline: '以埃斯顿六轴机器人为核心的技能实训与工艺教学平台。',
    summary:
      '涵盖搬运码垛、分拣、抛光打磨、装配与焊接等典型应用工艺，面向工业机器人操作与编程教学。',
    intro:
      '装置以六轴工业机器人为核心，包含搬运码垛、分拣、抛光打磨、装配和焊接等典型工业机器人应用工艺，不仅满足职业院校工业机器人操作和编程的教学需求，也更加适合作为职业技能竞赛平台。',
    introPoints: [
      '输入电源：AC220V±10％ 50Hz，装置容量＜3.5kVA',
      '平台尺寸：1760mm×960mm×1880mm',
      '工艺模块：搬运、码垛、分拣、打磨、装配、焊接等',
    ],
    detailPoints: [
      '输入电源：AC220V±10％ 50Hz，装置容量＜3.5kVA',
      '平台尺寸：1760mm×960mm×1880mm',
      '工艺模块：搬运、码垛、分拣、打磨、装配、焊接等',
      '安全保护：漏电保护，符合国家安全标准',
    ],
  },
  '焊接机器人工作站': {
    tagline: '工业机器人 + 焊接电源 + 柔性平台的一体化焊接实训系统。',
    summary:
      '支持直线、往复、月牙、圆圈等多种焊接路径与工艺参数实训，占地约 4000mm×3000mm×1800mm。',
    intro:
      '焊接机器人工作站由工业机器人、焊接电源、焊枪、三维柔性焊接平台与实训套件组成，适用于焊接机器人编程、工艺调试与质量评估教学。',
    detailPoints: [
      '输入电源：三相四线 AC380V±10% 50Hz',
      '装置容量：≤25kVA',
      '焊接方法：直线、往复、月牙、圆圈等多种轨迹',
      '安全保护：漏电、短路、过载保护装置齐全',
    ],
  },

  // —— 新能源与工业互联网 ——
  '太阳能光伏发电实验装置': {
    tagline: '双轴跟踪太阳能发电演示、设计与调试一体化实验系统。',
    summary:
      '由模拟光源跟踪装置和模拟能源控制系统组成，可用于演示、设计、安装和调试，支持双轴跟踪与能效采集实验。',
    intro:
      '本装置是一套既能演示又能设计、安装、调试的太阳能发电追踪系统。学生可通过调节模拟光源、跟踪角度与能源控制参数，理解光伏发电原理、双轴跟踪控制与能源管理流程，适用于新能源专业实训与实验室建设。',
    introPoints: [
      '输入电压：三相四线～380V±10% 50Hz，装置容量＜3kVA',
      '太阳能电池板：20W×4，调节高度 300mm',
      '跟踪方式：双轴，俯仰 180°、旋转 360°，精度＜±1.5°',
    ],
    detailPoints: [
      '模拟光源：1000W，连续运行范围 10°～170°，倾角 40°～80°',
      '跟踪控制：双轴自动跟踪，实时角度反馈与精度验证',
      '能源管理：发电数据采集、控制策略与系统联调',
      '实验内容：安装接线、参数设置、跟踪调试与故障排查',
    ],
  },
  '风能发电教学与监控系统': {
    tagline: '面向新能源教学与实验室建设的风能发电实训系统。',
    summary:
      '风能发电教学与监控系统可与能源管理平台联动，支持发电数据采集、运行监控与教学演示。',
    intro:
      '风能发电教学与监控系统适用于职业院校新能源技术、电气自动化等专业。系统可演示风能转换原理、发电过程与监控方法，并与新能源实验室综合管理平台对接，实现数据看板与远程监测。',
    detailPoints: [
      '教学演示：风能原理、发电过程与系统结构认知',
      '数据采集：风速、转速、电压、电流等运行参数',
      '监控联动：与能源管理平台、实训室看板对接',
      '扩展能力：可按项目补充额定功率、装置尺寸等规格',
    ],
  },
  '新能源实验室综合管理平台': {
    tagline: '新能源实训室设备联动、数据看板与教学演示管理平台。',
    summary:
      '用于新能源实训室的设备接入、数据采集、能效分析与实验看板，适合实训基地集中管理。',
    intro:
      '新能源实验室综合管理平台将太阳能、风能等实验装置统一接入，提供设备状态监控、实验数据采集、能效分析与教学看板展示，帮助院校建设一体化新能源实训基地。',
    detailPoints: [
      '设备接入：太阳能、风能等实验装置统一联网',
      '数据采集：发电功率、效率、环境参数实时采集',
      '能效分析：日/月/年报表、对比分析与导出',
      '教学看板：实验进度、设备状态与演示大屏',
    ],
  },
  '工业互联网综合实训平台': {
    tagline: '传感器、网关、云平台与工业 APP 一体化实训平台。',
    summary:
      '以温湿度、光照、压力、噪音、电流、电压等传感器为感知层，485 采集、PLC、网关与计算机为网络层，私有化部署应用层场景。',
    intro:
      '实训台采用私有化本地部署，可搭建环境监测、能源监控、生产过程监控、数据追溯等应用场景，训练和培养学生设备接入、数据采集、上云、数据可视化、数字孪生与工业 APP 开发能力。',
    introPoints: [
      '交流电源：单相～220V±10% 50Hz，装置容量＜2.0kVA',
      '外形尺寸：约 1880mm×880mm×1950mm',
      '安全保护：漏电保护、智能重合闸、接地保护',
    ],
    detailPoints: [
      '感知层：温湿度、光照度、大气压力、噪音、电流、电压等',
      '网络层：485 采集模块、PLC、无线路由、网关、计算机',
      '应用层：环境监测、能源监控、生产监控、数据追溯',
      '能力训练：数据采集、上云、可视化、数字孪生、工业 APP',
    ],
  },
  '工业 APP 与数据追溯平台': {
    tagline: '面向生产过程、环境与能源管理的工业应用层平台。',
    summary:
      '支持环境监测、能源监控、数据追溯与可视化看板，可与 485 采集、PLC、网关等设备联动，私有化本地部署。',
    intro:
      '工业 APP 与数据追溯平台是工业互联网综合实训平台的应用层延伸，也可独立部署于工厂或园区。平台支持自定义看板、移动端 APP 与数据追溯链路，满足产线数字化与教学演示双重需求。',
    detailPoints: [
      '环境监测：温湿度、光照、噪音等实时看板与历史曲线',
      '能源监控：电、水、气等分项计量与告警',
      '数据追溯：批次、工序、质检数据关联查询',
      '工业 APP：移动端巡检、审批与异常推送',
    ],
  },

  // —— 机器人 / AGV / 无人机 ——
  '四足巡检机器狗（轮足款）': {
    tagline: '轮足复合四足机器人，兼顾速度与复杂地形通过能力。',
    summary:
      '轮足复合结构在平坦路面高速移动，在楼梯、坡道等场景切换四足模式，适合长距离巡检与复杂地形运维。',
    intro:
      '轮足款四足巡检机器狗结合轮式移动效率与四足越障能力，适用于园区、工厂内外混合环境巡检。高仁科技提供载荷定制、任务规划、远程操控与数据管理平台一体化交付。',
    detailPoints: [
      '复合驱动：平坦区域轮式高速，复杂地形四足越障',
      '巡检载荷：可见光、红外、气体检测等模块化配置',
      '任务模式：自主巡检、定点值守与远程遥控',
      '数据回传：实时图传、巡检报告与缺陷标注',
    ],
  },
  '便携式机器人实训箱': {
    tagline: '集成机械臂、视觉与示教屏幕的便携式 AI 实训方案。',
    summary: '桌面级一体化平台，适合机器视觉、抓取编程与 AI 算法验证教学。',
    intro:
      '便携式机器人实训箱将轻量机械臂、视觉相机与示教屏幕集成于便携箱体，便于课堂演示与小组实验。支持视觉定位、轨迹规划与深度学习模型部署入门实训。',
    detailPoints: [
      '机械臂：轻量协作臂，支持示教与编程',
      '机器视觉：定位、识别、缺陷检测实验',
      '便携设计：适合课堂、竞赛与外出演示',
      '开放接口：支持 Python/C++ 二次开发',
    ],
  },
  '便携式 AI 开发箱（蓝面板）': {
    tagline: '机械臂 + AI 开发界面的便携式算法验证平台。',
    summary: '蓝面板人机界面，适合深度学习、机器视觉与机器人联调实验。',
    intro:
      '便携式 AI 开发箱配备直观蓝面板操作界面，集成机械臂与 AI 开发环境，学生可快速完成图像采集、模型训练推理与机器人动作联动实验。',
    detailPoints: [
      'AI 开发：模型训练、推理与部署流程演示',
      '视觉联动：相机图像驱动机械臂抓取',
      '界面友好：触摸屏参数配置与状态监控',
      '教学适用：人工智能、机器人专业基础课程',
    ],
  },
  '嵌入式 AI 开发箱': {
    tagline: '面向嵌入式算法与控制系统联调的 AI 开发平台。',
    summary: '适合边缘推理、控制器联调与嵌入式 AI 项目验证。',
    intro:
      '嵌入式 AI 开发箱聚焦边缘计算场景，支持在嵌入式控制器上部署轻量神经网络，并与 PLC、传感器等工业设备联调，适合高级实训与课题研发。',
    detailPoints: [
      '边缘推理：轻量模型在嵌入式设备上运行',
      '工业联调：与 PLC、传感器、执行器对接',
      '算法验证：目标检测、分类、回归等任务',
      '开发工具：提供 SDK 与示例工程',
    ],
  },
  'Delta 机器人实训工作站': {
    tagline: '并联 Delta 机器人实训与视觉抓取开发平台。',
    summary: '高速并联结构，适合分拣、包装、视觉定位等工艺实训。',
    intro:
      'Delta 机器人实训工作站采用并联机器人结构，运行速度快、重复定位精度高，配套视觉系统可完成动态抓取、分拣与包装模拟实验，适用于自动化、机器人专业教学。',
    detailPoints: [
      '并联结构：高速拾取、分拣与轨迹规划',
      '视觉系统：定位、识别与动态跟踪抓取',
      '工艺实训：包装、分拣、装配模拟',
      '编程调试：示教器与上位机联合调试',
    ],
  },
  'AI 实训平台': {
    tagline: '立式 AI 实训与演示平台，适合实验室固定部署。',
    summary: '竖式结构，集成机械臂、视觉与 AI 算力，适合课程与展示。',
    intro:
      'AI 实训平台采用立式机柜结构，集成工业级组件与 AI 算力单元，适合作为实验室核心设备开展机器视觉、深度学习与机器人集成课程，也可用于展厅技术演示。',
    detailPoints: [
      '立式部署：实验室固定安装，组件布局清晰',
      'AI 算力：支持 GPU 加速训练与推理',
      '集成度高：机械臂、相机、光源、控制器一体',
      '课程配套：可按专业方向定制实验项目',
    ],
  },
  '移动 AI 开发平台': {
    tagline: '小型移动底盘，适合 AI 算法与移动机器人联调。',
    summary: '移动底盘 + 视觉/传感器，适合 SLAM、导航与 AI 决策实验。',
    intro:
      '移动 AI 开发平台提供可编程移动底盘，搭载相机、激光雷达等传感器，支持 SLAM 建图、路径规划与 AI 决策算法验证，适合机器人、人工智能交叉实验。',
    detailPoints: [
      '移动底盘：差速/全向驱动，开放运动控制接口',
      '传感器：相机、激光雷达、IMU 等可选',
      '算法实验：SLAM、导航、目标跟踪与决策',
      '二次开发：ROS/SDK 支持上层应用开发',
    ],
  },
  '重载物流无人机': {
    tagline: '大载荷工业级多旋翼，适用于短途物流与应急投送。',
    summary: '可定制载重、续航与任务载荷，支持地面站任务规划与监控。',
    intro:
      '重载物流无人机面向园区、工地、应急等场景的物资转运需求。高仁科技提供机体定制、飞控调参、地面站软件与任务规划服务，可按载重、航程与环境条件专项设计。',
    detailPoints: [
      '大载荷：载重量按项目定制，满足工业转运需求',
      '飞行控制：自主起降、航线规划与避障',
      '地面站：任务下发、实时监控与日志回放',
      '安全设计：冗余保护与应急返航策略',
    ],
  },
  '工业巡检无人机': {
    tagline: '多镜头工业巡检无人机，适用于电力、石化、园区巡检。',
    summary: '可配置可见光、红外、变焦相机等载荷，支持缺陷识别与报告生成。',
    intro:
      '工业巡检无人机专为线路、杆塔、管道、光伏组件等巡检任务设计，支持多镜头切换、定点环绕与自动航线，配合图像分析可输出巡检报告与缺陷标注。',
    detailPoints: [
      '多载荷：可见光、红外、变焦相机可选',
      '巡检模式：自动航线、定点悬停、环绕拍摄',
      '图像分析：缺陷识别、对比与报告导出',
      '行业应用：电力、石化、光伏、园区安防',
    ],
  },
  '复合移动机器人': {
    tagline: 'AGV 底盘与协作机械臂一体化，移动 + 操作双能力。',
    summary: '适用于灵活工位的物料搬运、上下料与简单装配作业。',
    intro:
      '复合移动机器人将 AGV 移动底盘与协作机械臂集成，可在产线间自主移动并完成取放、装配等操作，适合柔性制造与物流实训场景。',
    detailPoints: [
      '移动导航：SLAM 自主导航与精确定位',
      '协作臂：安全协作、示教编程与视觉辅助',
      '应用场景：上下料、配送、简单装配',
      '系统对接：MES/WMS 任务下发与状态回传',
    ],
  },
  '辊筒搬运 AGV': {
    tagline: '辊筒顶升物料搬运平台，适合箱式/托盘物料输送。',
    summary: '辊筒与顶升机构结合，实现工位间物料自动转运。',
    intro:
      '辊筒搬运 AGV 通过辊筒机构与输送线或工位对接，实现物料自动装卸与转运，适用于包装线、装配线线边物流。',
    detailPoints: [
      '辊筒对接：与输送线、工位滚筒无缝衔接',
      '顶升搬运：潜伏或顶升式物料转运',
      '调度协同：多车调度与交通管制',
      '负载定制：额定负载与辊筒规格按项目设计',
    ],
  },
  '标准 AMR 移动底盘': {
    tagline: '通用 AMR 移动机器人底盘，开放 SDK 便于二次开发。',
    summary: '模块化底盘设计，支持多种驱动形式与上装扩展。',
    intro:
      '标准 AMR 移动底盘提供稳定的移动能力与开放接口，客户可快速集成机械臂、视觉、语音等上装设备，搭建复合机器人或专用移动平台。',
    detailPoints: [
      '模块化：驱动、电池、控制器模块化设计',
      '开放 SDK：运动控制、导航、任务接口',
      '精确定位：激光 SLAM / 二维码组合',
      '上装扩展：机械臂、货架、显示屏等',
    ],
  },
  '全向移动平台': {
    tagline: '全向驱动移动平台，适合室内狭窄空间灵活部署。',
    summary: '麦轮/全向轮结构，支持横移、原地旋转等灵活运动。',
    intro:
      '全向移动平台采用全向驱动方案，在狭窄通道、紧凑工位间具有出色机动性，适合实验室、展厅及柔性产线物料配送。',
    detailPoints: [
      '全向运动：横移、斜移、原地旋转',
      '紧凑设计：适应狭窄通道与密集工位',
      '导航方式：SLAM / 视觉 / 磁条可选',
      '应用：室内物流、巡检、教学演示',
    ],
  },
  '移动复合机器人': {
    tagline: '移动底盘 + 协作臂一体方案，柔性作业单元。',
    summary: '自主移动并完成取放、检测等操作，适合离散制造柔性产线。',
    intro:
      '移动复合机器人整合 AMR 底盘与协作机械臂，形成可自主移动的柔性作业单元，减少固定工位投入，适合多品种小批量生产与实训演示。',
    detailPoints: [
      '柔性部署：无需固定工位，按任务移动作业',
      '协作安全：人机协作，适合与人共线',
      '视觉辅助：定位、检测与质量判别',
      '任务联动：与 MES/WMS 系统对接',
    ],
  },

  // —— 非标自动化与案例 ——
  '自动化产线非标定制（1）': {
    tagline: '模块化自动装配产线，集成机器人、视觉与输送系统。',
    summary: '从工艺分析到设备制造调试的全流程非标产线定制，适合装配类制造场景。',
    intro:
      '自动化产线非标定制方案面向客户具体工艺需求，集成工业机器人、视觉检测、传感器、输送线与上位系统，实现上料、装配、检测、下料全流程自动化，支持柔性生产与信息化联动。',
    detailPoints: [
      '工艺分析：节拍测算、工位划分与方案论证',
      '机械设计：非标机构、夹具与输送线设计',
      '电控集成：PLC、机器人、视觉、HMI 联合调试',
      '信息化：MES/数据采集/看板可选对接',
    ],
  },
  '自动化产线非标定制（2）': {
    tagline: '包装检测一体化产线，视觉 + 机器人 + 输送联动。',
    summary: '涵盖检测、贴标、装箱、码垛等工序的整线集成方案。',
    intro:
      '包装检测一体化产线将视觉检测、贴标、装箱、码垛等工序串联，提高包装效率与质量一致性。高仁科技提供从方案设计、设备制造到现场交付的全流程服务。',
    detailPoints: [
      '视觉检测：外观、尺寸、条码读码与剔除',
      '包装工序：贴标、装箱、封箱、码垛联动',
      '柔性配置：多品种快速换型',
      '数据追溯：批次、检测数据与报表',
    ],
  },
  '自动化专机与工位集成': {
    tagline: '装配、检测、上料、包装等单元化工位定制开发。',
    summary: '围绕单一或复合工序开发自动化专机，可与现有产线对接。',
    intro:
      '自动化专机与工位集成服务针对客户产线中的瓶颈工位或新增工序，提供专用自动化设备设计与制造，包括机械、电气、视觉与上位系统，并与 MES、机器人、传感器等对接。',
    detailPoints: [
      '服务范围：工艺分析、机械设计、电控设计、集成调试、现场交付',
      '对接系统：MES / WMS / 机器人 / 视觉 / 传感器',
      '交付形式：单机、工位或产线段集成',
      '售后支持：培训、备件与升级改造',
    ],
  },

  // —— 教育类实训装备 ——
  '智能制造自动化生产线综合实训系统': {
    tagline: '传感器生产线为载体，覆盖装配、检测、包装、入库全流程。',
    summary:
      '由推板上料、振动上料、翻转机械手、协作机器人、立体仓库等组成，实现电极套装配至入库全自动生产全过程。',
    intro:
      '本实训系统以传感器生产线为载体，可实现电极套上料装配、铜电极上料装配、固定环上料装配、外壳上料装配、翻转、搬运、传输、加盖、拧盖、视觉检测、包装、入库等全自动生产全过程，是智能制造、机电一体化专业核心实训装备。',
    introPoints: [
      '输入电源：AC220V±10％ 50Hz，装置容量＜2kVA',
      '平台尺寸：2400mm×960mm×1780mm',
      '组成：推板/井式/振动上料、协作机器人、立体仓库等',
    ],
    detailPoints: [
      '上料装配：推板、振动、井式等多种上料机构',
      '搬运传输：翻转机械手、链条输送线、协作机器人',
      '检测包装：视觉检测台、加盖拧盖、包装工序',
      '仓储物流：堆垛机、立体仓库与入库管理',
    ],
  },
  '工业机器人系统运维实训平台': {
    tagline: '机器人本体、控制柜与示教盒一体化运维教学平台。',
    summary: '面向工业机器人系统运维职业技能培养，涵盖安装、调试、维护与故障排查。',
    intro:
      '平台由工业机器人本体、机器人底座、控制柜和示教盒组成，面向工业机器人运维教学与考核。学生可完成机器人安装接线、参数设置、日常维护、故障诊断与恢复等全流程实训。',
    introPoints: [
      '输入电源：AC220V±10％ 50Hz，装置容量＜2kVA',
      '外形尺寸：1450mm×1000mm×900mm',
      '组成：本体、底座、控制柜、示教盒',
    ],
    detailPoints: [
      '安装调试：本体安装、电气接线与上电检查',
      '参数配置：坐标系、工具、用户坐标设定',
      '日常维护：润滑、备份、电池更换等运维操作',
      '故障排查：常见报警处理与恢复流程训练',
    ],
  },
  '典型传感器技术实验箱': {
    tagline: '模块化透明传感器实验箱，加深原理与结构认知。',
    summary:
      '支持应变片、差动变压器、霍尔、温度等 12 项以上典型传感器实验，实验箱尺寸不小于 518mm×408mm×240mm。',
    intro:
      '实验箱采用模块化设计，传感器结构透明、可拆装，能加深学生对传感器原理和结构的认识。涵盖金属箔式应变片、差动变压器、电容式、霍尔、温度测量等经典实验项目，适合传感器技术基础课程。',
    introPoints: [
      '实验箱尺寸：518mm×408mm×240mm（不小于）',
      '实验项目：应变片、差动变压器、霍尔、温度等 12+ 项',
      '结构设计：透明可拆装，便于观察内部结构',
    ],
    detailPoints: [
      '应变测量：单臂、半桥、全桥与电子称实验',
      '差动变压器：性能测定、零点补偿与频率影响',
      '其他传感器：电容位移、霍尔、PT100、热敏电阻等',
      '适用课程：传感器原理、检测技术、自动化基础',
    ],
  },
  '工业传感器综合实验装置': {
    tagline: '工业传感器与工业模型结合的新一代应用实验设备。',
    summary:
      '将工业级传感器与工业模型有机结合，适合传感器应用技术与工业模型演示教学。',
    intro:
      '本实验平台装置将工业传感器、工业模型有机结合，可模拟接近开关、光电、光纤、磁性、温度、压力等工业现场常见传感应用，帮助学生从原理实验过渡到工业应用场景认知。',
    introPoints: [
      '输入电源：单相三线 220V±10% 50Hz，容量 180VA',
      '外形尺寸：1600mm×800mm×1400mm',
      '配置：多种工业传感器与工业模型场景',
    ],
    detailPoints: [
      '工业传感器：接近、光电、光纤、磁性、温度、压力等',
      '工业模型：模拟产线、仓储、检测等应用场景',
      '接线调试：传感器选型、安装与信号调理',
      '应用实验：逻辑控制、计数、定位与报警',
    ],
  },
  '高级电工实训考核装置': {
    tagline: '依据人社部鉴定标准设计，配备智能考核系统。',
    summary:
      '满足高级电工实训与考核需求，三相五线 AC380V 供电，负荷容量≤1kVA。',
    intro:
      '装置依据人社部最新职业技能鉴定标准设计，并配备智能考核系统，可满足各类院校高级电工实训考核需求。涵盖电气安装、故障排查、安全规范等核心技能训练。',
    introPoints: [
      '工作电源：三相五线 AC380V±10% 50Hz',
      '外形尺寸：880×880×1880mm，负荷容量≤1kVA',
      '安全保护：过流、过载、漏电保护，符合国标',
    ],
    detailPoints: [
      '智能考核：自动评分、计时与成绩记录',
      '实训内容：电气安装、接线、故障排查',
      '安全规范：符合国家安全标准与鉴定要求',
      '适用对象：高职、本科电工电子与自动化专业',
    ],
  },
  '电工电子技术综合实验装置': {
    tagline: '模块化挂箱结构，可自由搭建各类电工电子电路。',
    summary:
      '电源与仪器仪表独立挂箱，创新模块透明外壳，适合基础实验与创新电路搭建。',
    intro:
      '装置采用模块化结构形式，电源和仪器仪表全部采用独立挂箱式结构，创新模块采用注塑透明外壳，可自由搭建各种电路，适合电工电子课程基础实验与创新设计。',
    introPoints: [
      '输入电源：三相四线～380V±10% 50Hz，容量＜1.5kVA',
      '外形尺寸：1600mm×720mm×1560mm',
      '结构：挂箱式，透明创新模块',
    ],
    detailPoints: [
      '电源挂箱：多路可调直流、交流电源',
      '仪表挂箱：电压表、电流表、示波器等',
      '实验模块：模拟电路、数字电路、电机控制等',
      '创新搭建：自由组合，支持课程设计与竞赛',
    ],
  },
  '生产系统网络与通讯运维实践平台': {
    tagline: '工业网络、PROFINET/PROFIBUS 通讯与设备监控实训平台。',
    summary:
      '包含工业网络单元、网络管理单元、生产制造设备监控系统，满足多种工业网络通讯实验。',
    intro:
      '装置采用挂箱结构，包含工业网络单元、网络管理单元、生产制造设备监控系统，满足自动化技术电气控制、PLC 控制、变频控制、机器人控制、PROFINET、PROFIBUS 工业网络通讯等实验需求。',
    introPoints: [
      '输入电源：三相四线～380V±10% 50Hz，容量＜1.5kVA',
      '外形尺寸：1600mm×720mm×1650mm',
      '通讯协议：PROFINET、PROFIBUS 等',
    ],
    detailPoints: [
      '工业网络：拓扑搭建、地址分配与通讯调试',
      'PROFINET/PROFIBUS：主从站配置与数据交换',
      '设备监控：生产系统运行状态监测',
      '运维实训：故障诊断、网络维护与优化',
    ],
  },
  '可编程控制器实训装置': {
    tagline: 'PLC + 触摸屏 + 变频 + 步进 + PROFINET 综合实训平台。',
    summary:
      '挂箱式结构，涵盖电气控制、传感器、变频、步进与工业以太网总线技术实训。',
    intro:
      '本装置采用挂箱式机构，包含 PLC 控制系统、触摸屏、变频系统、步进等系统，可满足自动化技术领域电气控制、传感器、PLC 控制、变频、步进控制、PROFINET 工业以太网总线等技术实训。',
    introPoints: [
      '输入电源：三相四线～380V±10% 50Hz，容量＜1.5kVA',
      '外形尺寸：1600mm×720mm×1650mm',
      '组成：PLC、触摸屏、变频器、步进系统',
    ],
    detailPoints: [
      'PLC 编程：逻辑控制、定时计数、顺序控制',
      '触摸屏：HMI 画面设计与数据监控',
      '变频控制：参数设置、多段速与通讯控制',
      '步进系统：脉冲定位、加减速与回原点',
      'PROFINET：工业以太网总线配置与调试',
    ],
  },
  '机械装调技能实训考核装置': {
    tagline: '世赛/国赛标准积木式机械装调实训考核装置。',
    summary:
      '开放式积木结构，可训练传动机构安装、机械精度调整与工量具使用。',
    intro:
      '产品采用开放式结构设计，按世赛和人社部国赛标准设计，采用积木式结构，可自由搭建不同传动机构，采用万能机械搭接底板和工业器件，可训练和考核各种工量具使用、传动机构安装、机械精度调整。',
    introPoints: [
      '工作电源：三相五线 AC380V±10%',
      '外形尺寸：不小于 1500mm×800mm×1450mm',
      '保护功能：漏电、短路、接地保护',
    ],
    detailPoints: [
      '传动机构：齿轮、皮带、链传动等安装调整',
      '精度调整：同轴度、平行度、齿合间隙、皮带张紧等',
      '工量具：游标卡尺、千分尺、水平仪等使用考核',
      '适用赛事：世赛、国赛机械装调赛项训练',
    ],
  },

  // —— 教育服务类 ——
  '数字教材解决方案': {
    tagline: '数字教材出版、平台与服务三位一体的综合解决方案。',
    summary: '涵盖知名出版机构合作、多终端阅读平台与规划申报、资源建设等专业服务。',
    intro:
      '数字教材解决方案整合数字教材出版、数字教材平台与数字教材服务三大方向，帮助院校完成从内容创编、资源制作到平台上线、出版发行的全流程数字教材建设。',
    detailPoints: [
      '数字教材出版：知名出版机构、专业编辑团队、严格出版流程',
      '数字教材平台：多终端同步阅读、智能 AI 教学助手、全场景教学应用',
      '数字教材服务：规划教材申报、数字资源建设、专业知识培训',
    ],
  },
  '数字教材平台与阅读服务': {
    tagline: '跨终端数字教材阅读与教学服务联动平台。',
    summary: '支持 Android/iOS/Windows/macOS 等多终端，账号管理与内容资源按项目配置。',
    intro:
      '数字教材平台与阅读服务为院校师生提供统一的教材访问入口，支持在线阅读、笔记标注、作业考试与学习报告，并与教务管理系统数据互通。',
    detailPoints: [
      '终端支持：Android / iOS / Windows / macOS / PC / Pad / 手机',
      '阅读体验：同步进度、笔记、检索与离线缓存',
      '教学联动：作业考试、学习报告与资源统计',
      '平台规模：账号与内容数量按项目定制',
    ],
  },
  '数字教材定制出版流程服务': {
    tagline: '从选题论证到出版发行的 10 步全流程服务。',
    summary: '整合选题、创编、三审三校、编辑制作与质量检查，适用于院校教材开发。',
    intro:
      '高仁科技提供数字教材定制开发出版全流程服务，覆盖选题论证、需求确认、项目启动、内容创编、资源制作、写作指导、三审三校、编辑制作、质量检查与出版发行十个关键步骤。',
    detailPoints: [
      '流程 1-3：选题论证、需求确认、项目启动',
      '流程 4-7：内容创编、资源制作、写作指导、三审三校',
      '流程 8-10：编辑制作、质量检查、出版发行',
    ],
  },
  '数字教材平台功能模块': {
    tagline: '教师端、学生端、教务端与数据分析一体化平台。',
    summary: '用户服务、教学应用与数据分析三大系统，支撑全场景数字教材教学。',
    intro:
      '数字教材平台功能模块包含教师端、学生端、教务管理端、数据分析系统和教材发行系统，支持跨平台终端访问，满足院校数字教材教学与管理需求。',
    detailPoints: [
      '用户服务系统：教师端、学生端、教务管理端，跨平台终端支持',
      '教学应用系统：阅读平台、作业考试、学习报告、资源统计等',
      '数据分析系统：阅读时间、答题情况、学习进度、用户活跃度',
    ],
  },
  '专业教学资源库建设解决方案': {
    tagline: '对标国家标准的校级/区域/国家级资源库建设方案。',
    summary: '覆盖微课、仿真、试题、文本、视频、动画等多类型资源与申报推广服务。',
    intro:
      '专业教学资源库建设解决方案对标国家建设标准，以院校特色专业为基础，为客户提供课程体系与优质课程建设服务，打造具有院校特色的教学资源库。',
    detailPoints: [
      '资源库形态：校级资源库、区域性资源库、全国性资源库',
      '资源类型：微课、仿真、试题、文本、视频、动画资源',
      '建设服务：资源申报、资源建设、资源上传、资源推广',
    ],
  },
  '教学资源库项目案例': {
    tagline: '国家级专业教学资源库项目案例与建设方向展示。',
    summary: '宣传册案例部分汇总，适用于职业院校、本科院校与培训基地参考。',
    intro:
      '教学资源库项目案例展示高仁科技参与或支持的国家级、区域性专业教学资源库建设项目，涵盖建设思路、资源结构与推广成效，可为院校申报与建设提供参考。',
    detailPoints: [
      '内容来源：宣传册案例部分与项目实践经验',
      '适用院校：职业院校、本科院校、培训基地',
      '服务内容：申报咨询、资源建设、平台部署与推广',
    ],
  },
  '技能大赛设备改造与专家指导': {
    tagline: '世界职业院校技能大赛赛项设备改造与专家团队指导。',
    summary: '提供设备改造、技术培训、赛项指导与驻场/咨询等多种服务形式。',
    intro:
      '围绕世界职业院校技能大赛等赛事，高仁科技提供赛项设备改造、赛题理解、技术训练和专家指导服务，帮助院校提升竞赛成绩并反哺日常实践教学。',
    detailPoints: [
      '支持范围：设备改造、技术培训、赛项指导、专家服务',
      '适用对象：职业院校、本科院校、竞赛培训基地',
      '服务形式：驻场支持 / 方案咨询 / 联合备赛',
    ],
  },
  '创新类大赛技术研发合作': {
    tagline: '教育部/人社部创新类大赛技术研发与成果转化合作。',
    summary: '专业需求对接、创新思维培养、赛题融合与成果转化为四大合作重点。',
    intro:
      '在创新类大赛背景下，高仁科技与职业院校开展技术研发合作，深入了解各院校专业特点，提供定制化技术研发支持，组织创新工作坊与技术研讨，并将创新成果转化为产品、教材与在线课程。',
    detailPoints: [
      '专业需求对接：根据院校专业特点提供定制化技术研发支持',
      '创新能力培养：创新工作坊、技术研讨，激发创新思维',
      '成果转化应用：共同研发产品技术，沉淀教材与在线课程平台成果',
    ],
  },
  '校企科研技术合作服务': {
    tagline: '高校及职业院校横向课题、专利与成果转化合作。',
    summary: '实现企业与院校优势互补，输出专利技术、新工艺与新产品。',
    intro:
      '高校及职业院校与企业开展科研技术合作，实现优势互补与资源共享。企业借助学校理论研究能力加速创新，学校获得实践平台促进教师成果转化与学生技能提升，合作成果多以专利技术、新工艺、新产品等形式输出。',
    detailPoints: [
      '合作目标：优势互补、资源共享、共同攻克技术难题',
      '合作收益：提升技能、加速创新、降低研发成本、缩短研发周期',
      '成果形式：专利技术、新工艺、新产品、教学资源成果',
    ],
  },

  // —— 案例与客户 ——
  '原创工业级产品（1）': {
    tagline: '高仁科技原创研发的工业级产品组合（宣传册第 26 页）。',
    summary: '来源于公司原创技术研发与项目积累，可独立交付或组合到产线项目中。',
    intro:
      '原创工业级产品系列展示高仁科技在自动化、机器人、教学装备等方向的自主研发成果，可作为工业场景独立模块或与产线系统组合交付，详细型号与规格可在商务沟通中进一步确认。',
    detailPoints: [
      '内容来源：宣传册第 26 页整页展示',
      '研发方向：自动化、机器人、教学示范项目',
      '交付方式：独立模块或产线集成组合',
    ],
  },
  '原创工业级产品（2）': {
    tagline: '高仁科技原创工业级产品组合（宣传册第 27 页）。',
    summary: '涵盖工业设备、教学装备与定制集成等多方向原创产品。',
    intro:
      '第二组原创工业级产品进一步展示公司在工业设备、教学装备与定制集成方面的技术积累，适合作为示范项目、实训基地或产线升级的核心组件。',
    detailPoints: [
      '内容来源：宣传册第 27 页整页展示',
      '适用方向：工业设备、教学装备、定制集成',
      '参数状态：详细型号与规格按项目确认',
    ],
  },
  '机电一体化实训室效果图': {
    tagline: '机电一体化专业实训室空间规划与效果图方案。',
    summary: '涵盖布局、动线、设备点位与视觉风格，可用于投标与施工参考。',
    intro:
      '机电一体化实训室效果图展示专业实训空间整体规划，包括设备布局、电源点位、安全通道与教学区划分，帮助院校在建设前期直观评估方案可行性。',
    detailPoints: [
      '空间规划：设备布局、动线与教学区划分',
      '视觉呈现：效果图配合设备清单与说明',
      '后续服务：可深化设计并配套成套设备供货',
    ],
  },
  '数字化产线管理与运维实训基地效果图': {
    tagline: '数字化产线与运维实训基地整体建设效果图。',
    summary: '展示数字工厂、产线监控与运维实训一体化基地空间方案。',
    intro:
      '效果图呈现数字化产线管理与运维实训基地的整体建设思路，融合智能制造产线、MES/数字孪生、设备运维等要素，适合产教融合示范基地与高水平实训基地规划。',
    detailPoints: [
      '基地定位：数字化产线 + 运维管理综合实训',
      '空间要素：产线区、监控区、运维工位、示教区',
      '建设配套：设备清单、软件平台与施工落地',
    ],
  },
  '工业机器人与传感器实训室效果图': {
    tagline: '工业机器人实训室与传感器实训室组合空间方案。',
    summary: '双实训室组合案例，覆盖机器人编程与传感器应用两大方向。',
    intro:
      '本案例将工业机器人实训室与传感器实训室组合规划，形成「机器人操作编程 + 传感器检测应用」的复合实训空间，适合机电、自动化、机器人等专业共建共享。',
    detailPoints: [
      '机器人区：多工位机器人编程与工艺实训',
      '传感器区：工业传感器应用与检测实验',
      '组合优势：专业交叉、资源共享、成本优化',
    ],
  },
  '电工电子与维修电工实训室效果图': {
    tagline: '电工电子与维修电工双实训室空间案例。',
    summary: '兼顾基础电路实验与维修电工技能考核的实训空间设计。',
    intro:
      '电工电子与维修电工实训室效果图展示基础实验台与考核装置的布局方案，满足电工电子课程实验与维修电工技能鉴定双重需求，符合安全规范与鉴定标准。',
    detailPoints: [
      '电工电子区：模块化实验台与仪器仪表布局',
      '维修电工区：考核工位与安全规范设计',
      '适用对象：高职、本科电工电子与自动化专业',
    ],
  },
  '新能源与数字孪生仿真实训室效果图': {
    tagline: '新能源实训室 + 数字孪生仿真实训室组合案例。',
    summary: '融合新能源实验与数字孪生虚拟仿真的一体化实训空间。',
    intro:
      '新能源与数字孪生仿真实训室效果图展示新能源发电实验与数字孪生虚拟仿真相结合的实训空间，学生可在实物实验与虚拟仿真之间切换，加深对能源系统与数字化技术的理解。',
    detailPoints: [
      '新能源区：太阳能、风能等实验装置布局',
      '数字孪生区：虚拟产线、仿真监控与大屏展示',
      '教学价值：理实一体、虚实结合、前沿技术展示',
    ],
  },
  '企业及科协服务客户': {
    tagline: '高仁科技服务的企业客户与科协合作对象代表名录。',
    summary: '涵盖科教设备企业、制造企业与各级科协、科技馆等合作单位。',
    intro:
      '高仁科技长期服务杭州蔚来科教、江苏华明、天津钢管公司、天津方舟科技等企业客户，以及与天津市东丽区、南开区、北辰区科协及天津市科技馆等科协机构的合作，体现公司在工业与科普领域的服务广度。',
    detailPoints: [
      '企业客户：杭州蔚来科教、江苏华明、天津钢管公司、天津方舟科技等',
      '科协客户：天津市东丽区、南开区、北辰区科协及天津市科技馆等',
      '服务方向：设备定制、系统开发、科普与培训活动',
    ],
  },
  '职业院校与本科院校服务客户': {
    tagline: '高仁科技服务的职业院校与本科院校代表名录。',
    summary: '覆盖天津及全国多所高职、本科院校，服务实训建设、竞赛与科研合作。',
    intro:
      '高仁科技服务的院校包括天津市职业大学、天津机电职业技术学院、天津大学等职业院校与本科院校，服务方向涵盖实训室建设、系统开发、技能大赛支持与科研技术合作，形成广泛的产教融合合作网络。',
    detailPoints: [
      '职业院校：天津市职业大学、天津轻工职业技术学院、天津机电职业技术学院等',
      '本科院校：天津大学、南开大学、天津工业大学、河北工业大学等',
      '服务方向：实训建设、系统开发、竞赛支持、科研合作',
    ],
  },
}

function buildEducationIntro(category, product) {
  const name = product.name || category.label
  const hero = category.heroDesc || ''
  const specs = (category.featureBullets || []).slice(0, 3).join('；')
  return `${name}是天津高仁科技面向职业教育与本科院校实训教学推出的专业装备。${hero} ${specs ? `主要技术指标：${specs}。` : ''}产品按国家相关安全规范设计，支持理实一体化教学、职业技能等级认定与技能大赛训练，可根据院校专业方向进行模块增减与定制。`
}

function buildEducationDetailPoints(category) {
  return expandDetailFromBullets(category.featureBullets, [
    `产品分类：${category.parentLabel} · ${category.label}`,
    '支持院校实训室建设、课程开发、师资培训与竞赛备赛',
    '可根据场地条件、电源配置与专业方向定制模块',
    '提供安装调试、验收培训与实验指导书等配套（按项目约定）',
  ])
}

function buildRoboticsIntro(category, product) {
  return `${product.name}属于高仁科技「${category.label}」定制开发产品线。${product.desc || category.heroDesc || ''} 我们提供从需求分析、硬件选型、软件集成、现场调试到培训交付的全流程服务，并可与 MES、视觉、调度、工业互联网等系统对接，按客户场景配置传感器、末端工具与上层应用软件。`
}

function buildSystemModuleIntro(product, packEn) {
  const platform = packEn?.platformName || '自研云平台'
  const desc = product.tagline || product.desc || ''
  return `${product.name}是${platform}的核心功能模块。${desc} 模块数据与平台其他功能互通，支持私有化本地部署，按组织机构、回路或电站维度进行查询、分析、导出与权限管理。`
}

function buildSystemModuleDetailPoints(product, packEn) {
  const rows = product.specGroups?.[0]?.rows || []
  const fromSpec = rows.map(([k, v]) => `${k}：${v}`)
  return expandDetailFromBullets(fromSpec, packEn?.introPoints || [
    `所属平台：${packEn?.platformName || '自研云平台'}`,
    '部署方式：私有化 / 本地部署',
    '数据联动：与平台其他模块共享组织机构与权限体系',
  ])
}

function resolveProductEnrichment(product) {
  if (productEnrichmentByName[product.name]) {
    return productEnrichmentByName[product.name]
  }
  if (product.isSystemModule && product.systemPackId) {
    const packEn = systemPackEnrichment[product.systemPackId]
    if (packEn) {
      return {
        intro: buildSystemModuleIntro(product, packEn),
        detailPoints: buildSystemModuleDetailPoints(product, packEn),
        applications: packEn.applications,
        introPoints: packEn.introPoints,
      }
    }
  }
  return {}
}

function buildSpecTypeApplications(specType, parentLabel) {
  if (specType === 'education') {
    return [
      { title: '院校实训', desc: '高职、本科智能制造、机电、自动化等专业实训教学。' },
      { title: '技能鉴定', desc: '职业技能等级认定、1+X 证书与考核站点建设。' },
      { title: '竞赛训练', desc: '国赛、行业赛与校内技能大赛备赛。' },
    ]
  }
  if (specType === 'system') {
    return [
      { title: '工业现场', desc: `${parentLabel || '系统类'}产品的生产与管理场景。` },
      { title: '园区能源', desc: '多站点、多回路集中监控与报表分析。' },
      { title: '教学示范', desc: '院校云平台、能源或工业互联网实训。' },
    ]
  }
  if (specType === 'robotics' || specType === 'agv') {
    return [
      { title: '工业应用', desc: '制造、物流、巡检等现场落地项目。' },
      { title: '科研开发', desc: '算法验证、原型机与课题合作。' },
      { title: '教学展示', desc: '实验室、展厅与 open day 演示。' },
    ]
  }
  return [
    { title: '定制项目', desc: '按客户工艺与场地需求专项设计。' },
    { title: '集成交付', desc: '设备、软件、调试与培训一体化。' },
  ]
}

export function enrichProduct(category, product) {
  if (!category || !product) return product

  const byName = resolveProductEnrichment(product)
  const byCategory = categoryEnrichment[category.id] || {}
  const isEducation = category.specType === 'education' && category.detailMode === 'equipment'
  const isService = category.detailMode === 'service'
  const isSystem = product.productKind === 'system'
  const packEn = product.systemPackId ? systemPackEnrichment[product.systemPackId] : null

  let intro = byName.intro || product.intro
  let detailPoints = byName.detailPoints || product.detailPoints
  let summary = byName.summary || product.summary
  let tagline = byName.tagline || product.tagline

  if (!intro) {
    if (isEducation) intro = buildEducationIntro(category, product)
    else if (category.specType === 'robotics' || category.specType === 'agv')
      intro = buildRoboticsIntro(category, product)
    else if (isService)
      intro = `${product.name}是高仁科技「${category.label}」方向的专业服务。${category.heroDesc || product.desc} 我们提供从方案规划、内容/系统建设、实施交付到培训推广的全流程服务，可根据院校或企业实际需求定制交付内容与周期。`
    else if (isSystem && packEn)
      intro = buildSystemModuleIntro(product, packEn)
    else if (isSystem && product.tagline)
      intro = `${product.name}（${category.label}）。${product.tagline} 平台支持私有化部署，可按站点规模、回路数量与报表需求灵活配置模块，并提供实施培训与运维支持。`
    else
      intro = `${product.name}隶属于${category.parentLabel}「${category.label}」。${product.desc} ${category.heroDesc || ''}`.trim()
  }

  if (!detailPoints?.length) {
    if (isEducation) detailPoints = buildEducationDetailPoints(category)
    else if (isSystem && packEn) detailPoints = buildSystemModuleDetailPoints(product, packEn)
    else detailPoints = expandDetailFromBullets(category.featureBullets, [
      product.desc,
      `方案类型：${product.model || category.label}`,
      `服务类型：${product.load || '定制开发'}`,
      category.heroDesc,
    ].filter(Boolean))
  }

  if (!summary) {
    summary = intro.length > 120 ? `${intro.slice(0, 118)}…` : intro
  }

  if (!tagline) {
    tagline = product.desc?.length > 40 ? product.desc : `${product.name} — ${category.label}方向专业方案`
  }

  const applications = mergeArrays(
    byName.applications,
    product.applications,
    byCategory.applications,
    buildSpecTypeApplications(category.specType, category.parentLabel)
  )

  const deliveryItems = mergeArrays(
    byName.deliveryItems,
    product.deliveryItems,
    byCategory.deliveryItems,
    isEducation || category.specType === 'education' ? EDU_DELIVERY : DEFAULT_DELIVERY
  )

  const introPoints = byName.introPoints || product.introPoints || detailPoints.slice(0, 4)

  const highlightParams =
    byName.highlightParams ||
    product.highlightParams ||
    (product.highlights
      ? product.highlights.map((h) => ({ value: h.value, label: h.label }))
      : null)

  const enrichedDesc =
    summary.length > (product.desc?.length || 0) ? summary : product.desc || summary

  return {
    ...product,
    desc: enrichedDesc,
    tagline,
    summary,
    intro,
    introPoints,
    detailPoints,
    applications,
    deliveryItems,
    ...(highlightParams ? { highlightParams } : {}),
  }
}

export function sanitizeSpecGroups(specGroups) {
  if (!Array.isArray(specGroups)) return specGroups
  return specGroups.map((group) => ({
    ...group,
    rows: (group.rows || []).map(([k, v]) => [
      k,
      v === '待补充' || v === '占位' ? '按项目定制（商务沟通确认）' : v,
    ]),
  }))
}
